<script lang="ts" setup>
import type { DynamicContentBlocksFragment } from "~/lib/api-types";
import { getResponsiveImage } from "~/lib/helpers";

type GetPageableResponse = {
  pageableBySlug?: {
    model?: {
      title?: string | null;
      meta?: {
        title?: string | null;
        description?: string | null;
        canonical?: string | null;
        noindex?: boolean | null;
        og?: {
          title?: string | null;
          site_name?: string | null;
          type?: string | null;
        } | null;
        twitter?: {
          card?: string | null;
          site?: string | null;
        } | null;
      } | null;
      hero_settings?: {
        image?: any[];
      } | null;
      dynamic_content?: DynamicContentBlocksFragment[] | null;
    } | null;
  } | null;
};

const { locale } = useI18n();
const route = useRoute();
const siteConfig = useSiteConfig();

const { data, error } = await usePersistedFetch<GetPageableResponse>(
  "GetPageable",
  computed(() => ({ slug: "/" })),
  {
    key: computed(() => `GetPageable:${locale.value}:/`),
    watch: [() => locale.value],
  }
);

if (error.value) {
  console.error("[GetPageable] error:", error.value);
}

const model = computed(() => data.value?.pageableBySlug?.model ?? null);
const metaData = computed(() => model.value?.meta ?? {});
const title = computed(() => model.value?.title ?? "");
const heroSettings = computed(() => model.value?.hero_settings ?? null);
const dynamicContent = computed(() => model.value?.dynamic_content ?? []);

const heroImage = computed(() => heroSettings.value?.image?.[0] ?? null);

const pageAnchors = computed(() =>
  Array.isArray(dynamicContent.value)
    ? dynamicContent.value.reduce<string[]>((acc, block: any) => {
        if (block?.anchor) acc.push(block.anchor);
        return acc;
      }, [])
    : []
);

useHead({
  title: metaData.value?.title || title.value,
  meta: [
    { name: "description", content: metaData.value?.description || "" },
    { name: "og:title", content: metaData.value?.og?.title || title.value },
    { name: "og:site_name", content: metaData.value?.og?.site_name || "" },
    { name: "og:type", content: metaData.value?.og?.type || "" },
    { name: "twitter:card", content: metaData.value?.twitter?.card || "" },
    { name: "twitter:site", content: metaData.value?.twitter?.site || "" },
    {
      name: "robots",
      content: metaData.value?.noindex ? "noindex" : "index, follow",
    },
  ],
  link: [
    ...(heroImage.value
      ? [
          {
            rel: "preload",
            href: getResponsiveImage(heroImage.value, "3840"),
            as: "image" as const,
          },
        ]
      : []),
    {
      rel: "canonical",
      href:
        metaData.value?.canonical ||
        new URL(route.fullPath, siteConfig.url).href,
    },
  ],
});
</script>

<template>
  <HomepageHero :settings="heroSettings" />

  <AnchorNavigation :anchors="pageAnchors" v-if="pageAnchors.length" />

  <LazyDynamicContentResolver
    v-if="dynamicContent.length"
    :blocks="dynamicContent as DynamicContentBlocksFragment[]"
  />
</template>